import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink, Users, BookOpen, Upload, FileText, Loader2, Image as ImageIcon, Film, Trash2, X, Maximize2, Flag } from "lucide-react";
import { PAGES } from "../data/pageContent";
import { INFO_CARDS, FEATURES } from "../mock";
import AdSlot from "./AdSlot";
import Testimonials from "./Testimonials";
import BookShop from "./BookShop";
import { RetryImg } from "./RetryImg";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const btn =
  "inline-flex items-center justify-center gap-2 rounded bg-[#e0a82e] px-6 py-3 font-display text-sm font-600 uppercase tracking-wide text-[#1c1c1c] transition-colors duration-200 hover:bg-[#f2b93f]";

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-[#e0a82e] md:text-4xl">{block.text}</h2>;
    case "h3":
      return <h3 className="mt-2 font-display text-xl font-600 uppercase tracking-wide text-[#e0574c] md:text-2xl">{block.text}</h3>;
    case "p":
      return <p className="text-base leading-relaxed text-[#c8c8c8] md:text-lg">{block.text}</p>;
    case "strong":
      return <p className="font-display text-lg font-500 uppercase tracking-wide text-[#f0f0f0]">{block.text}</p>;
    case "note":
      return <p className="text-sm italic leading-relaxed text-[#a0a0a0]">{block.text}</p>;
    case "divider":
      return <hr className="my-2 border-white/10" />;
    case "button":
      return (
        <a href={block.href} target="_blank" rel="noopener noreferrer" className={btn}>
          {block.text} <ExternalLink className="h-4 w-4" />
        </a>
      );
    case "internalButton":
      return (
        <Link to={block.to} className={btn}>
          <BookOpen className="h-4 w-4" /> {block.text}
        </Link>
      );
    case "cta":
      return (
        <Link
          to="/community"
          className="inline-flex items-center justify-center gap-2 rounded border border-[#e0a82e] bg-transparent px-6 py-3 font-display text-sm font-600 uppercase tracking-wide text-[#e0a82e] transition-colors duration-200 hover:bg-[#e0a82e] hover:text-[#1c1c1c]"
        >
          <Users className="h-4 w-4" /> {block.text}
        </Link>
      );
    case "slang":
      return (
        <div className="space-y-3">
          {block.items.map((s, i) => (
            <div key={i} className="rounded-md border border-white/10 bg-[#242424] p-4">
              <span className="font-display text-base font-600 uppercase tracking-wide text-[#e0a82e]">{s.term}</span>
              <span className="mx-2 text-[#666]">&mdash;</span>
              <span className="text-sm text-[#c8c8c8]">{s.meaning}</span>
            </div>
          ))}
        </div>
      );
    case "states":
      return (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {block.items.map((st, i) => {
            const activeCls =
              "flex items-center justify-center rounded-md border border-[#e0a82e] bg-[#e0a82e] px-4 py-6 font-display text-base font-600 uppercase tracking-wide text-[#1c1c1c] transition-transform hover:scale-[1.03]";
            if (st.active && st.to)
              return (
                <Link key={i} to={st.to} className={activeCls}>
                  {st.label}
                </Link>
              );
            if (st.active && st.href)
              return (
                <a key={i} href={st.href} target="_blank" rel="noopener noreferrer" className={activeCls}>
                  {st.label}
                </a>
              );
            return (
              <div
                key={i}
                className="flex flex-col items-center justify-center rounded-md border border-white/10 bg-[#242424] px-4 py-6 font-display text-base font-600 uppercase tracking-wide text-[#8a8a8a]"
              >
                {st.label}
                <span className="mt-1 text-[10px] normal-case tracking-normal text-[#6a6a6a]">Coming soon</span>
              </div>
            );
          })}
        </div>
      );
    case "image":
      return (
        <div className="py-4" data-testid="content-image">
          <img
            src={block.src}
            alt={block.alt || "image"}
            className="mx-auto w-full max-w-3xl rounded-md border border-white/10 shadow-2xl"
            loading="lazy"
          />
        </div>
      );
    case "bookcover":
      return (
        <div className="flex justify-center py-2">
          <img
            src="https://i0.wp.com/incarcerated.com.au/wp-content/uploads/2026/09/my-book.png?fit=683%2C1024&ssl=1"
            alt="10 Years in 10 Prisons book cover"
            className="w-56 rounded-md border border-white/10 shadow-2xl"
            loading="lazy"
          />
        </div>
      );
    case "subscribe":
      return <SubscribeForm />;
    case "bookShop":
      return <BookShop />;
    case "testimonials":
      return <Testimonials />;
    case "buyEbook":
      return <BuyEbookButton />;
    case "donate":
      return <DonateForm />;
    case "contactForm":
      return <ContactForm />;
    case "uploadsWall":
      return <UploadsWall />;
    case "comingSoon":
      return (
        <button
          type="button"
          disabled
          className="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded border border-[#e0a82e]/40 bg-[#2a2a2a] px-6 py-3 font-display text-sm font-600 uppercase tracking-wide text-[#e0a82e]/70"
        >
          <BookOpen className="h-4 w-4" /> {block.text}
          <span className="ml-1 rounded bg-[#e0574c] px-2 py-0.5 text-[10px] font-600 uppercase tracking-wide text-white">
            Coming Soon
          </span>
        </button>
      );
    default:
      return null;
  }
};

const fmtSize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_BYTES = 500 * 1024 * 1024; // 500 MB

const REACTIONS = [
  { type: "like", emoji: "\uD83D\uDC4D", label: "Like" },
  { type: "love", emoji: "\u2764\uFE0F", label: "Love" },
  { type: "laugh", emoji: "\uD83D\uDE02", label: "Haha" },
  { type: "sad", emoji: "\uD83D\uDE22", label: "Sad" },
];

const readDeviceReactions = () => {
  try { return JSON.parse(localStorage.getItem("inc_reactions") || "{}"); } catch { return {}; }
};
const writeDeviceReaction = (id, type) => {
  const m = readDeviceReactions();
  if (type) m[id] = type; else delete m[id];
  localStorage.setItem("inc_reactions", JSON.stringify(m));
};

const ReactionBar = ({ item }) => {
  const [counts, setCounts] = useState(item.reactions || { like: 0, love: 0, laugh: 0, sad: 0 });
  const [mine, setMine] = useState(readDeviceReactions()[item.id] || null);

  const react = async (type) => {
    const prev = mine;
    let add = null, remove = null;
    if (prev === type) { remove = type; setMine(null); writeDeviceReaction(item.id, null); }
    else { add = type; remove = prev; setMine(type); writeDeviceReaction(item.id, type); }
    setCounts((c) => {
      const n = { ...c };
      if (add) n[add] = (n[add] || 0) + 1;
      if (remove) n[remove] = Math.max(0, (n[remove] || 0) - 1);
      return n;
    });
    try {
      const { data } = await api.post(`/letters/uploads/${item.id}/react`, { add, remove });
      if (data.reactions) setCounts(data.reactions);
    } catch (e) { /* keep optimistic value */ }
  };

  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`reactions-${item.id}`}>
      {REACTIONS.map((r) => (
        <button
          key={r.type}
          type="button"
          onClick={() => react(r.type)}
          data-testid={`react-${r.type}-${item.id}`}
          title={r.label}
          className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
            mine === r.type
              ? "border-[#e0a82e] bg-[#e0a82e]/15 text-[#e0a82e]"
              : "border-white/10 bg-[#1c1c1c] text-[#ccc] hover:border-white/30"
          }`}
        >
          <span className="text-sm leading-none">{r.emoji}</span>
          <span data-testid={`react-count-${r.type}-${item.id}`}>{counts[r.type] || 0}</span>
        </button>
      ))}
    </div>
  );
};

const UploadCard = ({ item, isAdmin, canReport, onOpen, onDelete, highlighted }) => {
  const src = `${BACKEND}${item.url}`;
  const enlargeable = item.kind === "image";
  const [reported, setReported] = useState(false);
  const [mediaError, setMediaError] = useState(false);

  const report = async () => {
    const reason = window.prompt("Why are you reporting this file? (optional)");
    if (reason === null) return; // cancelled
    try {
      await api.post(`/letters/uploads/${item.id}/report`, { reason });
      setReported(true);
    } catch (e) {
      if (e?.response?.status === 400) { setReported(true); }
      else { alert(e?.response?.data?.detail || "Could not report this file."); }
    }
  };

  return (
    <div
      id={`upload-${item.id}`}
      className={`group relative scroll-mt-28 overflow-hidden rounded-lg border bg-[#242424] transition-shadow duration-700 ${item.flagged && isAdmin ? "border-[#e0574c]" : "border-white/10"} ${highlighted ? "ring-2 ring-[#e0a82e] shadow-[0_0_32px_rgba(224,168,46,0.65)]" : ""}`}
      data-testid={`upload-card-${item.id}`}
      data-highlighted={highlighted ? "true" : undefined}
    >
      {item.flagged && isAdmin && (
        <span
          className="absolute left-2 top-2 z-10 flex items-center gap-1 rounded bg-[#e0574c] px-2 py-0.5 text-[11px] font-600 uppercase text-white"
          data-testid={`flag-badge-${item.id}`}
        >
          <Flag className="h-3 w-3" /> Flagged ({item.reportCount})
        </span>
      )}
      {isAdmin && (
        <button
          type="button"
          onClick={() => onDelete(item)}
          data-testid={`upload-delete-${item.id}`}
          title="Remove this upload"
          className="absolute right-2 top-2 z-10 rounded-full bg-black/75 p-2 text-white transition-colors hover:bg-[#e0574c]"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      <div
        className={`relative flex items-center justify-center bg-[#1a1a1a] ${enlargeable ? "cursor-pointer" : ""}`}
        style={{ minHeight: 160 }}
        onClick={enlargeable ? () => onOpen(item) : undefined}
        data-testid={enlargeable ? `upload-open-${item.id}` : undefined}
      >
        {item.kind === "image" && (
          <RetryImg src={`${process.env.REACT_APP_BACKEND_URL}/api/letters/thumb/${item.id}`} fallback={src} alt={item.caption || item.filename} className="h-48 w-full object-cover" loading="lazy" />
        )}
        {item.kind === "video" && !mediaError && (
          <video
            src={src}
            controls
            playsInline
            preload="metadata"
            onError={() => setMediaError(true)}
            className="h-48 w-full bg-black object-contain"
            data-testid={`upload-video-${item.id}`}
          />
        )}
        {item.kind === "audio" && !mediaError && (
          <audio src={src} controls onError={() => setMediaError(true)} className="w-full px-4" data-testid={`upload-audio-${item.id}`} />
        )}
        {(item.kind === "video" || item.kind === "audio") && mediaError && (
          <div className="flex flex-col items-center gap-2 p-6 text-center" data-testid={`media-fallback-${item.id}`}>
            <Film className="h-10 w-10 text-[#e0a82e]" />
            <p className="text-sm text-[#c8c8c8]">This file&rsquo;s format can&rsquo;t be played here.</p>
            <a
              href={src}
              download={item.filename}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 rounded bg-[#e0a82e] px-4 py-1.5 text-xs font-600 uppercase text-[#1c1c1c] hover:bg-[#f2b93f]"
            >
              Download to watch
            </a>
          </div>
        )}
        {item.kind === "document" && (
          <div className="flex flex-col items-center gap-2 p-8 text-[#e0a82e]">
            <FileText className="h-12 w-12" />
            <span className="text-xs uppercase tracking-wide text-[#999]">Document</span>
          </div>
        )}
        {enlargeable && (
          <span className="pointer-events-none absolute right-2 bottom-2 rounded bg-black/60 p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
            <Maximize2 className="h-4 w-4 text-white" />
          </span>
        )}
      </div>
      <div className="p-3">
        {item.caption && <p className="mb-1 line-clamp-2 text-sm font-500 text-[#f0f0f0]">{item.caption}</p>}
        <div className="flex items-center justify-between gap-2">
          <p className="min-w-0 truncate text-xs text-[#999]" title={item.filename}>
            by {item.uploaderName}{item.size ? ` \u00b7 ${fmtSize(item.size)}` : ""}
          </p>
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            download={item.filename}
            data-testid={`upload-download-${item.id}`}
            className="shrink-0 rounded bg-[#e0a82e] px-3 py-1.5 text-xs font-600 uppercase text-[#1c1c1c] transition-colors hover:bg-[#f2b93f]"
          >
            {item.kind === "document" ? "Download" : "Open"}
          </a>
        </div>

        <ReactionBar item={item} />

        {isAdmin && item.flagged && item.reports?.length > 0 && (
          <div className="mt-3 rounded border border-[#e0574c]/40 bg-[#e0574c]/10 p-2" data-testid={`report-reasons-${item.id}`}>
            <p className="mb-1 text-[11px] font-600 uppercase tracking-wide text-[#f08a80]">Reports ({item.reportCount})</p>
            <ul className="space-y-1">
              {item.reports.map((rp, i) => (
                <li key={i} className="text-xs text-[#d0b0ac]">
                  <span className="font-500 text-[#eaa]">{rp.username}</span>
                  {rp.reason ? `: ${rp.reason}` : " flagged this"}
                </li>
              ))}
            </ul>
          </div>
        )}

        {canReport && !isAdmin && (
          <button
            type="button"
            onClick={report}
            disabled={reported}
            data-testid={`report-btn-${item.id}`}
            className="mt-2 flex items-center gap-1 text-[11px] uppercase tracking-wide text-[#888] transition-colors hover:text-[#e0574c] disabled:text-[#666]"
          >
            <Flag className="h-3 w-3" /> {reported ? "Reported" : "Report"}
          </button>
        )}
      </div>
    </div>
  );
};

const Lightbox = ({ item, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!item) return null;
  const src = `${BACKEND}${item.url}`;
  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      data-testid="lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="lightbox-close"
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/25"
      >
        <X className="h-6 w-6" />
      </button>
      <div className="flex max-h-[85vh] max-w-5xl items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {item.kind === "image" && <img src={src} alt={item.caption || item.filename} className="max-h-[85vh] max-w-full object-contain" />}
        {item.kind === "video" && <video src={src} controls autoPlay className="max-h-[85vh] max-w-full bg-black" />}
      </div>
      {item.caption && (
        <p className="mt-4 max-w-2xl text-center text-base text-[#f0f0f0]" onClick={(e) => e.stopPropagation()}>{item.caption}</p>
      )}
      <p className="mt-1 text-sm text-[#999]">by {item.uploaderName}</p>
    </div>
  );
};

const UploadsWall = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [lightbox, setLightbox] = useState(null);

  const isAdmin = !!user?.is_admin;

  const load = async () => {
    try {
      const { data } = await api.get("/letters/uploads");
      setItems(data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => { load(); }, []);

  const location = useLocation();
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    const id = location.hash.startsWith("#upload-") ? location.hash.slice(8) : null;
    if (!id || !items.some((x) => x.id === id)) return;
    const t1 = setTimeout(() => {
      document.getElementById(`upload-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(id);
    }, 300);
    const t2 = setTimeout(() => setHighlightId(null), 3300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [items, location.hash]);

  const removeUpload = async (item) => {
    if (!window.confirm(`Remove "${item.caption || item.filename}" from the page? This cannot be undone.`)) return;
    try {
      await api.delete(`/letters/uploads/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not remove the upload.");
    }
  };

  const startUpload = async () => {
    setErr("");
    if (!file) { setErr("Please choose a file first."); return; }
    if (file.size > MAX_BYTES) { setErr("That file is larger than the 500 MB limit."); return; }
    if (!agreed) { setErr("Please tick the agreement box before uploading."); return; }

    setBusy(true);
    setProgress(0);
    const uploadId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`).replace(/[^a-f0-9-]/gi, "");
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    try {
      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("file", blob, file.name);
        await api.post("/letters/upload/chunk", fd, {
          onUploadProgress: (e) => {
            const frac = (i + e.loaded / (e.total || blob.size || 1)) / totalChunks;
            setProgress(Math.min(99, Math.round(frac * 100)));
          },
        });
      }
      const { data } = await api.post("/letters/upload/complete", {
        upload_id: uploadId,
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        total_chunks: totalChunks,
        size: file.size,
        caption: caption.trim(),
        agreed: true,
      });
      setProgress(100);
      setItems((prev) => [data, ...prev]);
      setFile(null);
      setCaption("");
      setAgreed(false);
      setOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="uploads-wall">
      {!open ? (
        <button
          type="button"
          data-testid="open-upload-btn"
          onClick={() => (user ? setOpen(true) : navigate("/login"))}
          className={btn}
        >
          <Upload className="h-4 w-4" /> Upload
        </button>
      ) : (
        <div className="rounded-lg border border-[#e0a82e]/30 bg-[#242424] p-5" data-testid="upload-panel">
          <div className="flex items-center justify-between">
            <h4 className="font-display text-base font-600 uppercase tracking-wide text-[#e0a82e]">Share a photo, video or document</h4>
            {!busy && (
              <button type="button" onClick={() => { setOpen(false); setErr(""); }} className="text-sm text-[#999] hover:text-white" data-testid="upload-cancel">
                Cancel
              </button>
            )}
          </div>

          <label
            htmlFor="letters-file-input"
            className="mt-4 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-white/15 bg-[#1c1c1c] px-4 py-8 text-center transition-colors hover:border-[#e0a82e]/50"
          >
            <Upload className="h-7 w-7 text-[#e0a82e]" />
            <span className="text-sm text-[#ccc]">
              {file ? file.name : "Click to choose any photo, video or document"}
            </span>
            {file && <span className="text-xs text-[#999]">{fmtSize(file.size)}</span>}
            <span className="text-[11px] text-[#777]">Up to 500 MB</span>
          </label>
          <input
            id="letters-file-input"
            type="file"
            className="hidden"
            data-testid="upload-file-input"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setErr(""); }}
            disabled={busy}
          />

          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={200}
            placeholder="Add a title or caption (optional)"
            data-testid="upload-caption-input"
            disabled={busy}
            className="mt-3 w-full rounded border border-white/15 bg-[#1c1c1c] px-4 py-3 text-[#eee] outline-none transition-colors focus:border-[#e0a82e]"
          />

          <label className="mt-4 flex items-start gap-3 text-sm text-[#c8c8c8]">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={busy}
              data-testid="upload-agree-checkbox"
              className="mt-1 h-4 w-4 accent-[#e0a82e]"
            />
            <span>
              I agree that I upload this content of my own free will. The site owner is <strong>not responsible</strong> for
              any content uploaded, and I accept full responsibility for what I share.
            </span>
          </label>

          {busy && (
            <div className="mt-4">
              <div className="h-2 w-full overflow-hidden rounded bg-[#1c1c1c]">
                <div className="h-full bg-[#e0a82e] transition-all duration-200" style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-1 text-xs text-[#999]">Uploading... {progress}%</p>
            </div>
          )}

          <button
            type="button"
            onClick={startUpload}
            disabled={busy || !file || !agreed}
            data-testid="upload-submit-btn"
            className={`${btn} mt-4 disabled:opacity-50`}
          >
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4" /> Upload now</>}
          </button>
          {err && <p className="mt-2 text-sm text-[#f08a80]" data-testid="upload-error">{err}</p>}
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-8">
          <h4 className="mb-4 font-display text-lg font-600 uppercase tracking-wide text-[#f0f0f0]">
            <ImageIcon className="mr-2 inline h-5 w-5 text-[#e0a82e]" />
            Uploads
          </h4>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="uploads-grid">
            {items.map((item) => (
              <UploadCard key={item.id} item={item} isAdmin={isAdmin} canReport={!!user} onOpen={setLightbox} onDelete={removeUpload} highlighted={highlightId === item.id} />
            ))}
          </div>
        </div>
      )}
      {items.length === 0 && !open && (
        <p className="mt-4 text-sm text-[#999]" data-testid="uploads-empty">
          No uploads yet. Be the first to share something.
        </p>
      )}

      <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
};

const BuyEbookButton = () => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const buy = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy(false);
    }
  };
  return (
    <div>
      <button onClick={buy} disabled={busy} className={`${btn} disabled:opacity-60`}>
        {busy ? "Redirecting to secure checkout..." : "BUY NOW \u2014 A$9.99"}
      </button>
      {err && <p className="mt-2 text-sm text-[#f08a80]">{err}</p>}
    </div>
  );
};

const DonateForm = () => {
  const PRESETS = [5, 10, 25, 50];
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const value = custom !== "" ? parseFloat(custom) : amount;

  const donate = async () => {
    setErr("");
    if (!value || value < 1) {
      setErr("Please enter an amount of at least A$1.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount: value,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start the donation. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="donate-form">
      <div className="flex flex-wrap gap-3">
        {PRESETS.map((p) => {
          const active = custom === "" && amount === p;
          return (
            <button
              key={p}
              type="button"
              data-testid={`donate-preset-${p}`}
              onClick={() => { setAmount(p); setCustom(""); }}
              className={`rounded border px-5 py-3 font-display text-sm font-600 uppercase tracking-wide transition-colors ${
                active
                  ? "border-[#e0a82e] bg-[#e0a82e] text-[#1c1c1c]"
                  : "border-white/15 bg-[#242424] text-[#e0a82e] hover:border-[#e0a82e]"
              }`}
            >
              A${p}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-2">
        <span className="font-display text-lg text-[#e0a82e]">A$</span>
        <input
          type="number"
          min="1"
          step="1"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="Other amount"
          data-testid="donate-custom-input"
          className="w-40 rounded border border-white/15 bg-[#242424] px-4 py-3 text-[#eee] outline-none transition-colors focus:border-[#e0a82e]"
        />
      </div>
      <button
        onClick={donate}
        disabled={busy}
        data-testid="donate-submit"
        className={`${btn} mt-5 disabled:opacity-60`}
      >
        {busy ? "Redirecting to secure checkout..." : `Donate A$${(value || 0).toFixed(2)}`}
      </button>
      {err && <p className="mt-2 text-sm text-[#f08a80]" data-testid="donate-error">{err}</p>}
    </div>
  );
};

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMsg("");
    try {
      const { data } = await api.post("/contact", form);
      setOk(true);
      setMsg(data.message || "Thanks! Your message has been sent.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      setOk(false);
      setMsg(err?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full rounded border border-white/15 bg-[#242424] px-4 py-3 text-[#eee] outline-none transition-colors focus:border-[#e0a82e]";

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="contact-form">
      <input
        type="text"
        required
        value={form.name}
        onChange={onChange("name")}
        placeholder="Your name"
        data-testid="contact-name"
        className={field}
      />
      <input
        type="email"
        required
        value={form.email}
        onChange={onChange("email")}
        placeholder="Your email address"
        data-testid="contact-email"
        className={field}
      />
      <textarea
        required
        rows={6}
        value={form.message}
        onChange={onChange("message")}
        placeholder="Your message"
        data-testid="contact-message"
        className={`${field} resize-y`}
      />
      <button type="submit" disabled={submitting} data-testid="contact-submit" className={`${btn} disabled:opacity-60`}>
        {submitting ? "Sending..." : "Send Message"}
      </button>
      {msg && (
        <p className={`text-sm ${ok ? "text-[#4caf7d]" : "text-[#f08a80]"}`} data-testid="contact-result">
          {msg}
        </p>
      )}
    </form>
  );
};

const SubscribeForm = () => {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMsg("");
    try {
      const { data } = await api.post("/subscribe", { email });
      setMsg(data.message || "Thanks for subscribing!");
      setEmail("");
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Could not subscribe. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email address"
          className="flex-1 rounded border border-white/15 bg-[#242424] px-4 py-3 text-[#eee] outline-none transition-colors focus:border-[#e0a82e]"
        />
        <button type="submit" disabled={submitting} className={`${btn} disabled:opacity-60`}>
          {submitting ? "Subscribing..." : "Subscribe"}
        </button>
      </form>
      {msg && <p className="mt-3 text-sm text-[#e0a82e]">{msg}</p>}
    </div>
  );
};

const RichPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = PAGES[slug];

  if (!page) {
    const info = INFO_CARDS.find((c) => c.to === `/${slug}`);
    const feature = FEATURES.find((f) => f.to === `/${slug}`);
    const title = info?.title || feature?.title || (slug || "").replace(/-/g, " ").toUpperCase();
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 md:px-8">
        <BackLink navigate={navigate} />
        <h1 className="font-display text-4xl font-700 uppercase tracking-wide text-[#e0a82e]">{title}</h1>
        <p className="mt-6 text-[#c4c4c4]">
          {info?.description || "This section is coming soon. We regularly update our site \u2014 please check back often for the latest information."}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-14 md:px-8">
      <BackLink navigate={navigate} />
      <h1 className="font-display text-4xl font-700 uppercase leading-tight tracking-wide text-[#e0a82e] md:text-5xl">
        {page.title}
      </h1>
      {page.subtitle && (
        <p className="mt-2 font-display text-lg uppercase tracking-wide text-[#e0574c] md:text-xl">
          {page.subtitle}
        </p>
      )}
      <div className="mt-8 flex flex-col gap-5">
        {page.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </div>
      <div className="-mx-4 mt-10 md:-mx-8">
        <AdSlot slot="content-bottom" />
      </div>
    </div>
  );
};

const BackLink = ({ navigate }) => (
  <button
    onClick={() => navigate("/")}
    className="mb-8 inline-flex items-center gap-2 font-display text-sm uppercase tracking-wide text-[#e0574c] transition-colors hover:text-[#e0a82e]"
  >
    <ArrowLeft className="h-4 w-4" /> Back to Home
  </button>
);

export default RichPage;
